import React, { createContext, FC, useContext } from 'react';
import { FilterSelectionType } from 'shared/types/graphql';
import { ConversationsType, useConversations } from '../hooks/useConversations';

const ConversationsContext = createContext<ConversationsType>({
  channelId: '',
  channelListFilter: {
    appointmentClinicEmployeeIds: [],
    appointmentRoomIds: [],
    appointmentTypeIds: [],
    assigneeIds: [],
    channelStatusIds: [],
    showArchived: false,
    tagNames: [],
    unread: false,
  },
  currentChannelFilterSelection: {
    filterSelectionType: FilterSelectionType.Private,
    filters: {
      appointmentClinicEmployeeIds: [],
      appointmentRoomIds: [],
      appointmentTypeIds: [],
      assigneeIds: [],
      channelStatusIds: [],
      showArchived: false,
      tagNames: [],
      unread: false,
    },
    isDeleted: false,
    name: 'All',
    id: '',
  },
  isSearching: false,
  handleChannelSelect: () => {
    throw new Error('handleChannelSelect not implemented');
  },
  handleChannelSearchInputChange: () => {
    throw new Error('handleChannelSearchInputChange not implemented');
  },
  handleFilterSelect: () => {
    throw new Error('handleFilterSelect not implemented');
  },
  handleChannelListViewSelect: () => {
    throw new Error('handleChannelListViewSelect not implemented');
  },
  handleShowArchivedToggle: () => {
    throw new Error('handleShowArchivedToggle not implemented');
  },
  clearChannelFilter: () => {
    throw new Error('clearChannelFilter not implemented');
  },
  handleSearchToggle: () => {
    throw new Error('handleSearchToggle not implemented');
  },
  showUnreadEmptyState: false,
  unreadChannelIds: [],
  isLoadingChannelList: false,
  unpinnedChannels: [],
  refetchChannels: async () => {
    throw new Error('refetchChannels not implemented');
  },
  hasNextPage: false,
  channelSearchInput: {},
  hasLoadedInitialChannelList: false,
  isFilterApplied: false,
  unreadMessageCountByChannel: {},
  searchResultsCount: 0,
  showEmptyState: false,
  showSearchResultsCount: false,
  pinnedChannels: [],
  filterPopoverKey: 0,
  isTeletriageConversationCenterOpen: false,
  onTeletriageConversationCenterClose: () => {
    throw new Error('onTeletriageConversationCenterClose not implemented');
  },
  onTeletriageConversationCenterOpen: () => {
    throw new Error('onTeletriageConversationCenterOpen not implemented');
  },
  teletriageConversationCenter: {
    clearFilters: () => {
      throw new Error('clearFilters not implemented');
    },
    conversations: [],
    filters: {
      clinicIds: [],
      conversationStatus: [],
      showArchived: false,
    },
    hasLoadedInitialList: false,
    isLoading: false,
    loadMoreConversations: () => {
      throw new Error('loadMoreConversations not implemented');
    },
    setFilters: () => {
      throw new Error('setFilters not implemented');
    },
    sortByLastMessageEnabled: false,
    totalConversationsCount: 0,
    toggleShowArchived: () => {
      throw new Error('toggleShowArchived not implemented');
    },
    isSearching: false,
    searchInput: undefined,
    setSearchInput: () => {
      throw new Error('setSearchInput not implemented');
    },
    isSearchingOpen: false,
    toggleSearch: () => {
      throw new Error('toggleSearch not implemented');
    },
  },
});

export const ConversationsContextProvider: FC<{ shouldShowTeletriageConversationCenter: boolean }> = ({
  shouldShowTeletriageConversationCenter,
  children,
}): JSX.Element => {
  const value = useConversations(shouldShowTeletriageConversationCenter);
  return <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>;
};

export const useConversationsContext = (): ConversationsType => useContext(ConversationsContext);
